/**
 * Client-side geo detection for the Gatsby (SSG) landing site.
 *
 * Gatsby builds static HTML at build time, so we can't read the visitor's IP
 * in SSR. We fetch Cloudflare's public trace endpoint once per session and
 * cache the result in sessionStorage. If detection fails, we default to
 * "strict", safer under ambiguity.
 *
 * Strict jurisdictions: California (US-CA), EU (EEA) member states, and UK.
 */

export type ConsentMode = "strict" | "relaxed"

const SESSION_KEY = "activeloop_geo_mode"

const EU_UK = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "GB",
])

const classify = (country: string, region: string): ConsentMode => {
  const c = country.toUpperCase()
  const r = region.toUpperCase()
  if (c === "US" && r === "CA") return "strict"
  if (EU_UK.has(c)) return "strict"
  if (!c) return "strict" // unknown geo → default safe
  return "relaxed"
}

/**
 * Non-async cache check; returns `null` if we haven't resolved geo this
 * session yet.
 */
export const readCachedConsentMode = (): ConsentMode | null => {
  if (typeof window === "undefined") return null
  try {
    const cached = window.sessionStorage.getItem(SESSION_KEY)
    if (cached === "strict" || cached === "relaxed") return cached
  } catch {
    // ignore
  }
  return null
}

const writeCache = (mode: ConsentMode) => {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(SESSION_KEY, mode)
  } catch {
    // ignore
  }
}

/**
 * Fetch geo from Cloudflare's public trace endpoint. Returns "strict" on
 * any failure, we never want to track a CA resident because a detection
 * call timed out.
 */
export const resolveConsentMode = async (): Promise<ConsentMode> => {
  const cached = readCachedConsentMode()
  if (cached) return cached

  // Short timeout, don't block the banner UX on a slow geo lookup.
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null
  const timeout = controller ? setTimeout(() => controller.abort(), 1500) : null

  try {
    const res = await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
      signal: controller?.signal,
      cache: "no-store",
    })
    if (!res.ok) throw new Error(`trace ${res.status}`)
    const text = await res.text()
    const entries = Object.fromEntries(
      text
        .split("\n")
        .filter(Boolean)
        .map((line) => {
          const i = line.indexOf("=")
          return [line.slice(0, i), line.slice(i + 1)]
        }),
    ) as Record<string, string>

    const country = entries.loc || ""
    // Cloudflare's public trace does not expose US state. For CA-accuracy we'd
    // want a paid IP-to-region service; until then, every US IP is treated as
    // strict (safe fallback, keeps CA residents covered at the cost of
    // showing the banner to non-CA US visitors).
    const region = country === "US" ? "CA" : ""

    const mode = classify(country, region)
    writeCache(mode)
    return mode
  } catch {
    writeCache("strict")
    return "strict"
  } finally {
    if (timeout) clearTimeout(timeout)
  }
}
